import React from 'react';

/**
 * Web-only showcase: Tashkeela is a mobile app, so on desktop we present it
 * inside iPhone + Android phone frames side by side, each running the real
 * app (auto-signed-in via `?embed=1`) so visitors can try every feature.
 *
 * This is a `.web.tsx` file, so it renders real DOM (div/iframe/img) through
 * react-dom - never bundled or evaluated on native.
 */

const FONT_STACK = "'Space Grotesk', 'Inter', system-ui, -apple-system, sans-serif";
// Always point at the PUBLIC production alias (absolute), never a relative path.
// On a protected preview deployment, a relative `/?embed=1` inherits the
// preview origin and hits Vercel's auth wall (which can show a framed Google
// 403). The production alias has no deployment protection and no X-Frame-Options,
// so it frames cleanly from any parent.
const EMBED_SRC = 'https://tashkeela.vercel.app/?embed=1';

const VOLT = '#22D3FF';
const VOLT_BRIGHT = '#5FE9FF';
const BG = '#05050E';
const TEXT = '#F1F2F8';
const MUTED = '#9698BE';

function DeviceFrame({ variant }: { variant: 'ios' | 'android' }) {
  const isIos = variant === 'ios';
  const outerRadius = isIos ? 48 : 32;
  const innerRadius = outerRadius - 10;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <div
        style={{
          position: 'relative',
          width: 348,
          height: 720,
          padding: 11,
          borderRadius: outerRadius,
          background: 'linear-gradient(160deg, #1a1a36, #07070f)',
          boxShadow:
            '0 40px 90px rgba(0,0,0,0.65), inset 0 0 0 1.5px rgba(34,211,255,0.16), inset 0 0 0 6px #05060a',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            borderRadius: innerRadius,
            overflow: 'hidden',
            background: BG,
          }}
        >
          <iframe
            title={isIos ? 'Tashkeela on iPhone' : 'Tashkeela on Android'}
            src={EMBED_SRC}
            allow="clipboard-write"
            style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
          />
          {isIos ? (
            <div
              style={{
                position: 'absolute',
                top: 9,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 104,
                height: 24,
                borderRadius: 16,
                background: '#05060a',
                zIndex: 5,
                pointerEvents: 'none',
              }}
            />
          ) : (
            <div
              style={{
                position: 'absolute',
                top: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 9,
                height: 9,
                borderRadius: 9,
                background: '#05060a',
                boxShadow: '0 0 0 2px rgba(255,255,255,0.06)',
                zIndex: 5,
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      </div>
      <span
        style={{
          fontFamily: FONT_STACK,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          color: MUTED,
        }}
      >
        {isIos ? 'iPhone' : 'Android'}
      </span>
    </div>
  );
}

export function Showcase() {
  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        boxSizing: 'border-box',
        background: `radial-gradient(120% 60% at 50% -10%, rgba(34,211,255,0.10), transparent 55%), ${BG}`,
        color: TEXT,
        fontFamily: FONT_STACK,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 24px 56px',
        gap: 30,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontSize: 40, fontWeight: 700, letterSpacing: -1 }}>Tashkeela</span>
          <span style={{ fontSize: 30, fontWeight: 700, color: VOLT_BRIGHT }}>تشكيلة</span>
        </div>
        <span style={{ fontSize: 16, color: MUTED, textAlign: 'center', maxWidth: 560 }}>
          Live football, fantasy, and predictions - bilingual AR/EN. Try the real app below;
          you are signed in automatically.
        </span>
        <a
          href={EMBED_SRC}
          style={{
            marginTop: 4,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            height: 40,
            padding: '0 18px',
            borderRadius: 999,
            background: VOLT,
            color: '#04121A',
            fontSize: 14,
            fontWeight: 700,
            textDecoration: 'none',
          }}
        >
          Open full screen ↗
        </a>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: 56,
        }}
      >
        <DeviceFrame variant="ios" />
        <DeviceFrame variant="android" />
      </div>

      <span style={{ fontSize: 12.5, color: MUTED, letterSpacing: 0.3 }}>
        Demo account · shared showcase data · every feature is interactive
      </span>
    </div>
  );
}
